/**
 * Dots pattern — interactive dotted background (shared module).
 *
 * Unifies the fullscreen comingsoon/maintenance effect and the section-scoped
 * .sectors effect: a canvas grid of dots that "heat up" near the pointer
 * (base → red #e94025 → orange #ff9727) with lerp smoothing and cubic falloff.
 *
 * Auto-init targets:
 *  1. #cs-pattern            — existing fixed canvas (comingsoon/maintenance),
 *                              "viewport" preset (spacing 36, radius 3).
 *  2. .sectors               — canvas injected into the section, "section"
 *                              preset (spacing 24, radius 1.2 — matches the
 *                              CSS dots-pattern mixin used as no-JS fallback).
 *  3. [data-dots-pattern]    — any container, for future pages. Options via
 *                              data attributes (all optional):
 *                              data-dots-spacing="36" data-dots-radius="3"
 *                              data-dots-glow="130"
 *                              data-dots-base="38,44,58"
 *                              data-dots-mid="233,64,37"
 *                              data-dots-peak="255,151,39"
 *
 * Injected canvases get the .js-dots-canvas class and the host gets
 * .has-dots-canvas (used by CSS to disable the static ::before fallback).
 * With prefers-reduced-motion the grid is drawn once, statically.
 * Animation only runs while the target is in the viewport.
 *
 * Motion (Google-Stitch-like — behaviour only, palette unchanged):
 *  - Dots react instantly to pointer movement (fast attack).
 *  - When the pointer stops, they hold briefly (HOLD_MS), then fade smoothly
 *    back to base over FADE_MS with an ease — organic, never abrupt. This is a
 *    time-based "activity" envelope multiplied onto the positional heat, so the
 *    reveal recedes even while the pointer sits still.
 *  - Text readability: the dots are erased (destination-out) behind headings /
 *    paragraphs inside the host with a soft feather, so typography stays crisp
 *    without recolouring anything elsewhere. Auto-targets h1–h6 + p; override
 *    with data-dots-mask="<selector>", disable with data-dots-mask="off".
 *  - The RAF loop sleeps once fully settled (idle) and wakes on the next move.
 */
(function () {
	'use strict';

	// Motion timing (ms) and smoothing — premium defaults, tunable per host via
	// data-dots-hold / data-dots-fade.
	var HOLD_MS = 260; // linger before the fade-out starts (subtle delay)
	var FADE_MS = 900; // duration of the smooth fade back to base
	var ATTACK_LERP = 0.28; // heat rising  → instant, snappy reveal
	var RELEASE_LERP = 0.14; // heat falling → gentle spatial settle
	var HEAT_EPSILON = 0.004; // below this a dot counts as "cold" (sleep test)

	// Text mask feather.
	var MASK_PAD_EM = 0.35; // padding around each text box, in em of its font-size
	var MASK_BLUR = 16; // soft-edge blur (CSS px)

	// Non-text mask shapes (opt-in via data-dots-mask-shape, es. icone Lottie
	// quadrate): padding e raggio proporzionali al box, non alla type size.
	var MASK_ICON_PAD_RATIO = 0.1; // pad = 10% del lato minore del box
	var MASK_ICON_RADIUS_RATIO = 0.34; // raggio squircle = 34% del box "padded"

	var PRESETS = {
		viewport: {
			spacing: 36,
			radius: 3,
			glow: 130,
			base: { r: 38, g: 44, b: 58 },
			mid: { r: 233, g: 64, b: 37 },
			peak: { r: 255, g: 151, b: 39 },
		},
		section: {
			spacing: 24,
			radius: 1.2,
			glow: 130,
			// ≈ rgba(255,255,255,.08) su #0b0e14 — stessa resa del mixin CSS
			base: { r: 31, g: 34, b: 40 },
			mid: { r: 233, g: 64, b: 37 },
			peak: { r: 255, g: 151, b: 39 },
		},
	};

	var reduceMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
	// movedAt = -Infinity → nothing is lit until the first real pointer move.
	var pointer = { x: -9999, y: -9999, movedAt: -Infinity };
	var pointerBound = false;
	// Instances that can be woken when the pointer moves after an idle sleep.
	var instances = [];

	function markPointerMove() {
		pointer.movedAt = now();
		for ( var i = 0; i < instances.length; i++ ) {
			instances[ i ].wake();
		}
	}

	function bindPointer() {
		if ( pointerBound ) {
			return;
		}
		pointerBound = true;
		window.addEventListener( 'mousemove', function ( e ) {
			pointer.x = e.clientX;
			pointer.y = e.clientY;
			markPointerMove();
		} );
		window.addEventListener( 'touchmove', function ( e ) {
			var t = e.touches[ 0 ];
			pointer.x = t.clientX;
			pointer.y = t.clientY;
			markPointerMove();
		}, { passive: true } );
		document.addEventListener( 'mouseleave', function () {
			pointer.x = pointer.y = -9999;
		} );
		window.addEventListener( 'touchend', function () {
			pointer.x = pointer.y = -9999;
		} );
	}

	var now = ( window.performance && window.performance.now )
		? function () { return window.performance.now(); }
		: function () { return Date.now(); };

	function lerp( a, b, t ) {
		return a + ( b - a ) * t;
	}

	// Cubic ease-out falloff
	function falloff( t ) {
		return 1 - t * t * t;
	}

	function easeInOutCubic( t ) {
		return t < 0.5 ? 4 * t * t * t : 1 - Math.pow( -2 * t + 2, 3 ) / 2;
	}

	/**
	 * Time-based activity envelope in [0,1] driven by pointer inactivity.
	 * 1 while moving / within the hold window, then eases 1 → 0 across the fade.
	 * @param {number} idle  ms since the last pointer movement
	 * @param {number} hold  linger duration before the fade starts
	 * @param {number} fade  fade-out duration
	 */
	function activityAt( idle, hold, fade ) {
		if ( idle <= hold ) {
			return 1;
		}
		if ( idle >= hold + fade ) {
			return 0;
		}
		return easeInOutCubic( 1 - ( idle - hold ) / fade );
	}

	// "233,64,37" → {r,g,b}
	function parseRgb( str, fallback ) {
		if ( ! str ) {
			return fallback;
		}
		var parts = str.split( ',' ).map( Number );
		if ( parts.length !== 3 || parts.some( isNaN ) ) {
			return fallback;
		}
		return { r: parts[ 0 ], g: parts[ 1 ], b: parts[ 2 ] };
	}

	function makeHeatToColor( base, mid, peak ) {
		return function ( heat ) {
			var r, g, b, t;
			if ( heat <= 0.5 ) {
				t = heat * 2;
				r = lerp( base.r, mid.r, t );
				g = lerp( base.g, mid.g, t );
				b = lerp( base.b, mid.b, t );
			} else {
				t = ( heat - 0.5 ) * 2;
				r = lerp( mid.r, peak.r, t );
				g = lerp( mid.g, peak.g, t );
				b = lerp( mid.b, peak.b, t );
			}
			return 'rgb(' + Math.round( r ) + ',' + Math.round( g ) + ',' + Math.round( b ) + ')';
		};
	}

	// Rounded-rect path (ctx.roundRect isn't universal yet).
	function roundRectPath( c, x, y, w, h, r ) {
		r = Math.min( r, w / 2, h / 2 );
		c.beginPath();
		c.moveTo( x + r, y );
		c.arcTo( x + w, y, x + w, y + h, r );
		c.arcTo( x + w, y + h, x, y + h, r );
		c.arcTo( x, y + h, x, y, r );
		c.arcTo( x, y, x + w, y, r );
		c.closePath();
	}

	/**
	 * @param {Element} host   Element measured for size (section, or the canvas itself).
	 * @param {HTMLCanvasElement} canvas
	 * @param {Object} opts    spacing/radius/glow/base/mid/peak/hold/fade/maskSelector
	 */
	function createInstance( host, canvas, opts ) {
		var ctx = canvas.getContext( '2d' );
		if ( ! ctx ) {
			return;
		}

		var heatToColor = makeHeatToColor( opts.base, opts.mid, opts.peak );
		var dpr = Math.min( window.devicePixelRatio || 1, 2 );
		var dots = [];
		var rafId = null;
		var visible = false;

		// Offscreen mask (built on resize / font load) used to erase the dots
		// behind text via destination-out. Null when the host has no matching
		// text (e.g. the fixed #cs-pattern canvas, whose text lives elsewhere).
		var maskCanvas = null;

		function buildDots( w, h ) {
			dots = [];
			var cols = Math.ceil( w / opts.spacing ) + 1;
			var rows = Math.ceil( h / opts.spacing ) + 1;
			for ( var row = 0; row < rows; row++ ) {
				for ( var col = 0; col < cols; col++ ) {
					dots.push( { x: col * opts.spacing, y: row * opts.spacing, heat: 0 } );
				}
			}
		}

		// Cache text boxes (host-relative) and bake the feathered erase mask.
		// Called only on resize / fonts.ready — never per frame.
		function buildMask( w, h ) {
			maskCanvas = null;
			if ( opts.maskSelector === 'off' ) {
				return;
			}
			var els = host.querySelectorAll( opts.maskSelector );
			if ( ! els.length ) {
				return; // e.g. host === canvas (text is not a descendant)
			}

			var canvasRect = canvas.getBoundingClientRect();
			var rects = [];
			var range = document.createRange();
			for ( var i = 0; i < els.length; i++ ) {
				// Elementi non testuali (es. icona Lottie quadrata) opt-in via
				// data-dots-mask-shape: si maschera il box intero con padding e
				// raggio proporzionali alla sua dimensione (squircle morbido),
				// non i line-box del testo (che per un SVG sono vuoti/instabili).
				var shape = els[ i ].getAttribute( 'data-dots-mask-shape' );
				var boxes;
				var pad;
				var corner;
				if ( shape ) {
					var eb = els[ i ].getBoundingClientRect();
					var side = Math.min( eb.width, eb.height );
					pad = side * MASK_ICON_PAD_RATIO;
					corner = ( side + pad * 2 ) * MASK_ICON_RADIUS_RATIO;
					boxes = [ eb ];
				} else {
					// Line-box del testo (Range), non il box dell'elemento: un h2/p
					// block-level è largo quanto la riga intera, mentre i client
					// rect del range abbracciano solo il testo renderizzato,
					// riga per riga (anche centrato o multi-riga).
					range.selectNodeContents( els[ i ] );
					var lineRects = range.getClientRects();
					boxes = lineRects.length ? lineRects : [ els[ i ].getBoundingClientRect() ];
					var fs = parseFloat( window.getComputedStyle( els[ i ] ).fontSize ) || 16;
					pad = fs * MASK_PAD_EM;
					corner = fs * 0.4; // corner radius scales with type size
				}
				for ( var k = 0; k < boxes.length; k++ ) {
					var r = boxes[ k ];
					if ( r.width === 0 || r.height === 0 ) {
						continue; // hidden / collapsed
					}
					rects.push( {
						x: r.left - canvasRect.left - pad,
						y: r.top - canvasRect.top - pad,
						w: r.width + pad * 2,
						h: r.height + pad * 2,
						r: corner,
					} );
				}
			}
			range.detach();
			if ( ! rects.length ) {
				return;
			}

			var mc = maskCanvas || document.createElement( 'canvas' );
			mc.width = Math.max( 1, Math.round( w * dpr ) );
			mc.height = Math.max( 1, Math.round( h * dpr ) );
			var mctx = mc.getContext( '2d' );
			mctx.setTransform( 1, 0, 0, 1, 0, 0 );
			mctx.clearRect( 0, 0, mc.width, mc.height );
			mctx.filter = 'blur(' + ( MASK_BLUR * dpr ) + 'px)';
			mctx.fillStyle = '#fff';
			for ( var j = 0; j < rects.length; j++ ) {
				var b = rects[ j ];
				roundRectPath( mctx, b.x * dpr, b.y * dpr, b.w * dpr, b.h * dpr, b.r * dpr );
				mctx.fill();
			}
			mctx.filter = 'none';
			maskCanvas = mc;
		}

		function resize() {
			// Solo il backing store: la dimensione visiva resta al CSS
			// (fixed inset 0 per #cs-pattern, absolute inset 0 per .js-dots-canvas),
			// così il canvas continua a seguire viewport/host senza style inline.
			var w = host.clientWidth;
			var h = host.clientHeight;
			canvas.width = w * dpr;
			canvas.height = h * dpr;
			ctx.setTransform( dpr, 0, 0, dpr, 0, 0 );
			buildDots( w, h );
			buildMask( w, h );
		}

		/**
		 * Draw one frame. `activity` (0–1) scales the positional heat so the
		 * reveal recedes on pointer inactivity. Returns the hottest dot heat so
		 * the caller can decide when the grid has fully settled.
		 */
		function render( mx, my, activity ) {
			var w = canvas.width / dpr;
			var h = canvas.height / dpr;
			ctx.clearRect( 0, 0, w, h );

			var maxHeat = 0;
			for ( var i = 0; i < dots.length; i++ ) {
				var dot = dots[ i ];
				var dx = dot.x - mx;
				var dy = dot.y - my;
				var dist = Math.sqrt( dx * dx + dy * dy );

				var targetHeat = ( dist < opts.glow ? falloff( dist / opts.glow ) : 0 ) * activity;
				// Asymmetric smoothing: fast attack (instant reveal), gentle release.
				var l = targetHeat > dot.heat ? ATTACK_LERP : RELEASE_LERP;
				dot.heat += ( targetHeat - dot.heat ) * l;

				ctx.beginPath();
				ctx.arc( dot.x, dot.y, opts.radius + dot.heat * 2, 0, Math.PI * 2 );
				ctx.fillStyle = heatToColor( dot.heat );
				ctx.fill();

				if ( dot.heat > maxHeat ) {
					maxHeat = dot.heat;
				}
			}

			// Erase the dots behind text (soft feather) — readability first.
			if ( maskCanvas ) {
				ctx.save();
				ctx.setTransform( 1, 0, 0, 1, 0, 0 );
				ctx.globalCompositeOperation = 'destination-out';
				ctx.drawImage( maskCanvas, 0, 0 );
				ctx.restore();
			}

			return maxHeat;
		}

		function drawStatic() {
			render( -99999, -99999, 0 );
		}

		function draw() {
			var rect = canvas.getBoundingClientRect();
			var activity = activityAt( now() - pointer.movedAt, opts.hold, opts.fade );
			var maxHeat = render( pointer.x - rect.left, pointer.y - rect.top, activity );

			// Sleep once the grid is cold; wake() resumes on the next pointer move.
			// Attack is instant, so a cold frame means nothing here is lit or about
			// to light — a section far from the pointer sleeps after one frame.
			if ( maxHeat < HEAT_EPSILON ) {
				rafId = null;
				return;
			}
			rafId = requestAnimationFrame( draw );
		}

		// Restart the loop after an idle sleep (called on pointer move / resize).
		function wake() {
			if ( reduceMotion || ! visible || rafId !== null ) {
				return;
			}
			rafId = requestAnimationFrame( draw );
		}

		function observeSize( onResize ) {
			if ( 'ResizeObserver' in window ) {
				new ResizeObserver( onResize ).observe( host );
			} else {
				window.addEventListener( 'resize', onResize );
			}
		}

		if ( reduceMotion ) {
			// Static grid (still masked behind text), no listeners/animation.
			observeSize( function () {
				resize();
				drawStatic();
			} );
			resize();
			drawStatic();
			return;
		}

		bindPointer();
		instances.push( { wake: wake } );

		// Animate only while on screen.
		var observer = new IntersectionObserver( function ( entries ) {
			entries.forEach( function ( entry ) {
				if ( entry.isIntersecting ) {
					visible = true;
					wake();
				} else {
					visible = false;
					if ( rafId !== null ) {
						cancelAnimationFrame( rafId );
						rafId = null;
					}
				}
			} );
		}, { threshold: 0 } );
		observer.observe( host );

		observeSize( function () {
			resize();
			wake(); // redraw the resized frame even if currently asleep
		} );
		resize();

		// Text metrics can shift once webfonts load — rebuild the mask then.
		if ( document.fonts && document.fonts.ready ) {
			document.fonts.ready.then( function () {
				buildMask( host.clientWidth, host.clientHeight );
				wake();
			} );
		}

		// Anche lo split in righe dell'header (src/js/header-text.js) sposta il
		// testo: le righe partono da sotto la maschera e risalgono. I rect qui
		// sono in cache, quindi si ricostruiscono quando il testo si è fermato.
		window.addEventListener( 'am:textsettled', function () {
			buildMask( host.clientWidth, host.clientHeight );
			wake();
		} );
	}

	// Number from a data attribute, falling back to a default (0 is valid).
	function numOr( raw, fallback ) {
		var n = parseFloat( raw );
		return isNaN( n ) ? fallback : n;
	}

	// Options from data attributes, on top of a preset.
	function optsFromDataset( el, preset ) {
		var d = el.dataset;
		return {
			spacing: parseFloat( d.dotsSpacing ) || preset.spacing,
			radius: parseFloat( d.dotsRadius ) || preset.radius,
			glow: parseFloat( d.dotsGlow ) || preset.glow,
			hold: numOr( d.dotsHold, HOLD_MS ),
			fade: numOr( d.dotsFade, FADE_MS ),
			maskSelector: d.dotsMask || 'h1, h2, h3, h4, h5, h6, p',
			base: parseRgb( d.dotsBase, preset.base ),
			mid: parseRgb( d.dotsMid, preset.mid ),
			peak: parseRgb( d.dotsPeak, preset.peak ),
		};
	}

	// Inject a canvas into a host container and init.
	function initContainer( host, preset ) {
		if ( host.querySelector( '.js-dots-canvas' ) ) {
			return; // already initialised
		}
		var canvas = document.createElement( 'canvas' );
		canvas.className = 'js-dots-canvas';
		canvas.setAttribute( 'aria-hidden', 'true' );

		if ( window.getComputedStyle( host ).position === 'static' ) {
			host.style.position = 'relative';
		}
		host.prepend( canvas );
		host.classList.add( 'has-dots-canvas' );

		createInstance( host, canvas, optsFromDataset( host, preset ) );
	}

	// 1. Fixed canvas already in the markup (comingsoon / maintenance).
	var fixedCanvas = document.getElementById( 'cs-pattern' );
	if ( fixedCanvas ) {
		createInstance( fixedCanvas, fixedCanvas, optsFromDataset( fixedCanvas, PRESETS.viewport ) );
	}

	// 2. Sections + generic opt-in containers.
	var hosts = document.querySelectorAll( '.sectors, [data-dots-pattern]' );
	for ( var i = 0; i < hosts.length; i++ ) {
		initContainer( hosts[ i ], PRESETS.section );
	}
}());
